import { useState, useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store/modules/rootReducer'
import { ICycleData } from '../types/cycleSession'
import { planningApi } from '../services/api'

interface UseCycleSessionsResult {
  data: ICycleData | null
  loading: boolean
  error: string | null
  refetch: () => void
}

/**
 * Hook para buscar e gerenciar dados das sessões de um ciclo de estudos
 * @param scheduleId - ID do plano de estudos
 */
export const useCycleSessions = (scheduleId: string | null): UseCycleSessionsResult => {
  const [data, setData] = useState<ICycleData | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Obtém o currentPlanId do Redux para validação
  const currentPlanId = useSelector((state: RootState) => state.studyPlan.currentPlanId)

  // Ref para controlar requisições em andamento e evitar race conditions
  const abortControllerRef = useRef<AbortController | null>(null)

  const fetchSessions = useCallback(async () => {
    // Cancela requisição anterior se existir
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    if (!scheduleId) {
      setData(null)
      return
    }

    // Valida se o scheduleId corresponde ao plano atual no Redux
    // Isso evita requisições para planos que foram deletados
    if (currentPlanId && scheduleId !== currentPlanId) {
      return
    }

    setLoading(true)
    setError(null)

    abortControllerRef.current = new AbortController()

    try {
      const response = await planningApi.getSessionsByPlanId(scheduleId)
      setData(response)
    } catch (err: any) {
      // Ignora erros de requisições canceladas
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        return
      }
      setError(err?.response?.data?.message || 'Erro ao carregar sessões do ciclo')
      console.error('Error fetching cycle sessions:', err)
    } finally {
      setLoading(false)
    }
  }, [scheduleId, currentPlanId])

  useEffect(() => {
    fetchSessions()

    return () => {
      // Cleanup: cancela requisição pendente ao desmontar ou quando deps mudam
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [fetchSessions])

  return {
    data,
    loading,
    error,
    refetch: fetchSessions,
  }
}
